
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Activity,
    Target,
    Zap,
    TrendingUp,
    Brain,
    History,
    Building2
} from "lucide-react";
import { Link } from "react-router-dom";
import AiLayout from "../../components/analyze/AiLayout";
import StatsCard from "../../components/ai-dashboard/StatsCard";
import SmartRecommendations from "../../components/ai-dashboard/SmartRecommendations";
import RecentAnalyses from "../../components/ai-dashboard/RecentAnalyses";
import TradingInsights from "../../components/ai-dashboard/TradingInsights";

// Mock Data (To be replaced with API)
const mockStats = {
    totalAnalysis: 142,
    winRate: 78,
    activeSignals: 12,
    weeklyProfit: "+15.4%"
};

const mockRecommendations = [
    {
        id: 1,
        asset_name: "Gold",
        asset_symbol: "XAU/USD",
        recommendation_type: "forex",
        recommendation_action: "BUY",
        current_price: 2345.50,
        expected_return_percent: 2.1,
        time_horizon: "Intraday",
        confidence_score: 92
    },
    {
        id: 2,
        asset_name: "Bitcoin",
        asset_symbol: "BTC/USDT",
        recommendation_type: "crypto",
        recommendation_action: "STRONG_BUY",
        current_price: 68450.00,
        expected_return_percent: 5.2,
        time_horizon: "Short-term",
        confidence_score: 94
    },
    {
        id: 3,
        asset_name: "Crude Palm Oil",
        asset_symbol: "FCPO",
        recommendation_type: "stock", // Using stock type for FCPO for now as it's an exchange asset
        recommendation_action: "SELL",
        current_price: 3950,
        expected_return_percent: 3.5,
        time_horizon: "Swing",
        confidence_score: 88
    },
    {
        id: 4,
        asset_name: "Euro",
        asset_symbol: "EUR/USD",
        recommendation_type: "forex",
        recommendation_action: "HOLD",
        current_price: 1.0850,
        expected_return_percent: 0.5,
        time_horizon: "Scalp",
        confidence_score: 85
    },
    {
        id: 5,
        asset_name: "Silver",
        asset_symbol: "XAG/USD",
        recommendation_type: "forex",
        recommendation_action: "BUY",
        current_price: 28.40,
        expected_return_percent: 4.8,
        time_horizon: "Mid-term",
        confidence_score: 89
    }
];

const mockRecentAnalyses = [
    {
        id: 101,
        image_url: "https://placehold.co/600x400/1e293b/white?text=ETH+Chart",
        trading_type: "spot",
        analysis_type: "crypto",
        crypto_pair: "ETH/USDT",
        pattern_detected: "Ascending Triangle",
        risk_level: "medium",
        confidence_score: 85,
        entry_price: 3450.00,
        take_profit_1: 3600.00,
        stop_loss: 3380.00,
        created_date: new Date().toISOString()
    },
    {
        id: 102,
        stock_code: "FCPO",
        stock_name: "Crude Palm Oil Futures",
        analysis_type: "stock",
        image_url: "https://placehold.co/600x400/1e293b/white?text=FCPO+Chart",
        risk_level: "high",
        confidence_score: 82,
        entry_price: 3920,
        take_profit_1: 4100,
        stop_loss: 3850,
        created_date: new Date(Date.now() - 86400000).toISOString()
    }
];

export default function AiTradingDashboard() {
    const [stats, setStats] = useState(mockStats);
    const [isGenerating, setIsGenerating] = useState(false);
    const [dashLoading, setDashLoading] = useState(false);
    const [recommendations, setRecommendations] = useState(mockRecommendations);
    const [pricesLoading, setPricesLoading] = useState(true);

    // Fetch real prices on mount
    React.useEffect(() => {
        const fetchPrices = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
                const API_URL = API_BASE.endsWith('/api/v1') ? API_BASE : `${API_BASE}/api/v1`;
                const response = await fetch(`${API_URL}/prices`);
                const data = await response.json();

                if (data.success && data.data) {
                    // Update recommendations with real prices
                    setRecommendations(prev => prev.map(rec => {
                        const symbolKey = rec.asset_symbol.replace('/', '').toUpperCase();
                        const priceData = data.data[symbolKey];

                        if (priceData?.price) {
                            return {
                                ...rec,
                                current_price: priceData.price
                            };
                        }
                        return rec;
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch prices:', error);
            } finally {
                setPricesLoading(false);
            }
        };

        fetchPrices();
        // Refresh prices every 5 minutes
        const interval = setInterval(fetchPrices, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleRefreshRecommendations = () => {
        setIsGenerating(true);
        // Simulate API call
        setTimeout(() => {
            setIsGenerating(false);
        }, 2000);
    };

    const handleRefreshAnalysis = () => {
        setDashLoading(true);
        setTimeout(() => {
            setDashLoading(false);
        }, 1500);
    };

    return (
        <AiLayout>
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Learn 2 Trade Hub</h1>
                    <p className="text-slate-400">
                        Advanced market analysis powered by L2T
                    </p>
                </div>
                <div className="flex gap-3">
                    <Link to="/ai-trading/analyze">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-gradient-to-r from-emerald-500 to-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-emerald-500/20 flex items-center gap-2"
                        >
                            <Brain className="w-5 h-5" />
                            New Analysis
                        </motion.button>
                    </Link>
                </div>
            </div>

            {/* Quick Actions Navigation */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Link to="/ai-trading/analyze">
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-6 rounded-2xl border border-white/5 hover:border-blue-500 transition-all cursor-pointer h-full flex flex-col items-center text-center group"
                        style={{ background: '#1A1D24' }}
                    >
                        <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-blue-500 transition-colors">
                            <TrendingUp className="w-7 h-7 text-blue-400 group-hover:text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Markets Overview</h3>
                        <p className="text-slate-400 text-sm">Instant technical analysis for all markets</p>
                    </motion.div>
                </Link>

                <Link to="/ai-trading/stock-analysis">
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-6 rounded-2xl border border-white/5 hover:border-red-500 transition-all cursor-pointer h-full flex flex-col items-center text-center group"
                        style={{ background: '#1A1D24' }}
                    >
                        <div className="w-14 h-14 bg-red-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-red-500 transition-colors">
                            <Building2 className="w-7 h-7 text-red-400 group-hover:text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Saham Malaysia 🇲🇾</h3>
                        <p className="text-slate-400 text-sm">Analisis saham Bursa Malaysia + berita fundamental</p>
                    </motion.div>
                </Link>

                <Link to="/ai-trading/history">
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-6 rounded-2xl border border-white/5 hover:border-purple-500 transition-all cursor-pointer h-full flex flex-col items-center text-center group"
                        style={{ background: '#1A1D24' }}
                    >
                        <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-purple-500 transition-colors">
                            <History className="w-7 h-7 text-purple-400 group-hover:text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Track Results</h3>
                        <p className="text-slate-400 text-sm">Monitor trading performance</p>
                    </motion.div>
                </Link>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatsCard
                    title="Total Analysis"
                    value={stats.totalAnalysis}
                    icon={Activity}
                    gradient="from-blue-500 to-cyan-500"
                    trend="+12 this month"
                    iconColor="text-blue-400"
                    borderColor="hover:border-blue-500"
                />
                <StatsCard
                    title="AI Win Rate"
                    value={`${stats.winRate}%`}
                    icon={Target}
                    gradient="from-emerald-500 to-green-500"
                    trend="+2.4% this week"
                    iconColor="text-emerald-400"
                    borderColor="hover:border-emerald-500"
                />
                <StatsCard
                    title="Active Signals"
                    value={stats.activeSignals}
                    icon={Zap}
                    gradient="from-purple-500 to-pink-500"
                    trend="+3 new today"
                    iconColor="text-purple-400"
                    borderColor="hover:border-purple-500"
                />
                <StatsCard
                    title="Weekly Trend"
                    value={stats.weeklyProfit}
                    icon={TrendingUp}
                    gradient="from-orange-500 to-red-500"
                    trend="Strong Bullish"
                    iconColor="text-orange-400"
                    borderColor="hover:border-orange-500"
                />
            </div>

            {/* Smart Recommendations */}
            <SmartRecommendations
                recommendations={recommendations}
                isGenerating={isGenerating}
                onRefresh={handleRefreshRecommendations}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content Area - Recent Analysis */}
                <div className="lg:col-span-2">
                    <RecentAnalyses
                        analyses={mockRecentAnalyses}
                        isLoading={dashLoading}
                        onRefresh={handleRefreshAnalysis}
                        onCardClick={(analysis) => console.log("View analysis", analysis)}
                    />
                </div>

                {/* Sidebar Area - Insights */}
                <div className="space-y-8">
                    <TradingInsights analyses={[...mockRecentAnalyses, ...mockRecommendations]} />
                </div>
            </div>
        </AiLayout>
    );
}
